import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { submitLeadWithGeoLocation, getCountryInfo } from '../services/centralLeadApi';
import { countryCodes } from '../utils/countryCodes';

const InquiryModal = ({ isOpen, onClose, buttonContext = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    description: ''
  });

  const [countryCode, setCountryCode] = useState('+1');
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [isLoadingGeo, setIsLoadingGeo] = useState(false);

  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });

  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch geolocation when modal opens
  useEffect(() => {
    if (isOpen && !detectedCountry) {
      fetchGeoLocation();
    }
  }, [isOpen]);

  const fetchGeoLocation = async () => {
    setIsLoadingGeo(true);
    try {
      const countryInfo = await getCountryInfo();
      if (countryInfo) {
        setDetectedCountry(countryInfo);
        console.log('Detected country:', countryInfo);
        
        // Auto-set country code based on detected country
        const matchedCountry = countryCodes.find(c => c.country === countryInfo.countryCode);
        if (matchedCountry) {
          setCountryCode(matchedCountry.code);
          console.log('Auto-set country code to:', matchedCountry.code, matchedCountry.name);
        }
      }
    } catch (error) {
      console.error('Failed to fetch geolocation:', error);
    } finally {
      setIsLoadingGeo(false);
    }
  };

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone validation
  const validatePhone = (phone) => {
    // Validate based on country code
    const fullPhone = countryCode + ' ' + phone;
    const phoneRegex = /^\+\d{1,3}[\s\-\.]?\(?\d{3}\)?[\s\-\.]?\d{3}[\s\-\.]?\d{4}$/;
    return phoneRegex.test(fullPhone) && phone.replace(/[^\d]/g, '').length >= 10;
  };

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address (e.g., user@example.com)' }));
    } else {
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const handlePhoneBlur = () => {
    if (formData.phone && !validatePhone(formData.phone)) {
      setErrors(prev => ({ ...prev, phone: 'Please enter a valid 10-digit phone number' }));
    } else {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check terms agreement
    if (!agreedToTerms) {
      toast.error('Please agree to the terms before submitting');
      return;
    }

    // Validate before submission
    let hasErrors = false;
    const newErrors = { email: '', phone: '' };

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
      hasErrors = true;
    }

    if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      toast.error('Please fix the errors before submitting');
      return;
    }

    setIsLoading(true);

    try {
      const leadData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        countryCode: countryCode,
        country: countryCodes.find(c => c.code === countryCode)?.country || 'US',
        description: formData.description
      };

      const result = await submitLeadWithGeoLocation(leadData, null, buttonContext);
      
      if (result.success) {
        toast.success(result.message || 'Your inquiry has been submitted successfully!');
        // API will handle redirect automatically to /application/main?customerid=X
      } else {
        toast.error(result.message || 'Failed to submit. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('An error occurred. Please try again later.');
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 animate-in fade-in duration-200 overflow-y-auto">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] max-w-4xl w-full my-4 animate-in zoom-in-95 duration-200 relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 sm:top-3 sm:right-3 p-2.5 sm:p-2 bg-white/90 hover:bg-white rounded-full transition-all duration-200 hover:scale-110 z-10 group shadow-lg"
          aria-label="Close modal"
        >
          <X className="h-5 w-5 sm:h-4 sm:w-4 text-gray-700 group-hover:text-gray-900" />
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left Side - Image with Modern Overlay */}
          <div 
            className="relative bg-cover bg-center h-[100px] sm:h-[140px] lg:h-auto"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d?q=80&w=2070&auto=format&fit=crop)'
            }}
          >
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#012169]/90 via-[#8B1587]/85 to-[#AB2346]/90"></div>
            
            {/* Content */}
            <div className="relative h-full flex flex-col justify-center p-3 sm:p-4 lg:p-8">
              <div>
                <div className="inline-flex items-center gap-1 bg-[#F97316] text-[#012169] px-2 py-0.5 rounded-full font-bold text-[10px] sm:text-xs mb-1.5 sm:mb-3">
                  <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="hidden sm:inline">10,000+ Brands Protected</span>
                  <span className="sm:hidden">10,000+ Brands</span>
                </div>
                
                <h2 className="text-base sm:text-xl lg:text-3xl font-extrabold text-white mb-1 sm:mb-2 leading-tight">
                  FREE IP Protection Strategy
                </h2>
                <p className="text-[10px] sm:text-xs lg:text-sm text-white/90 mb-1.5 sm:mb-4 leading-tight sm:leading-relaxed hidden sm:block">
                  Worth $500 - Limited spots available this month
                </p>
              
                {/* Bottom Section - Benefits */}
                <div className="space-y-1 sm:space-y-2 hidden sm:flex sm:flex-col">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 bg-[#F97316] rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-[#012169]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-white text-xs sm:text-sm font-semibold">Save up to $5,000 on filing fees</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 bg-[#F97316] rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-[#012169]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-white text-xs sm:text-sm font-semibold">Response in 24 hours guaranteed</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 bg-[#F97316] rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-[#012169]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-white text-xs sm:text-sm font-semibold">98% approval rate for clients</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="p-3 sm:p-6 lg:p-7 bg-gradient-to-br from-gray-50 to-white">
            <div className="mb-2 sm:mb-4">
              <div className="flex items-center gap-1.5 mb-1.5 sm:mb-3">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#F97316] rounded-full animate-pulse"></div>
                <span className="text-[10px] sm:text-xs font-bold text-[#F97316] uppercase tracking-wide">5 Spots Left Today</span>
              </div>
              <h3 className="text-base sm:text-lg lg:text-xl font-bold text-[#012169] mb-0.5 sm:mb-1">
                Get Your Free Strategy Call
              </h3>
              <p className="text-[10px] sm:text-xs text-gray-600 hidden sm:block">
                Join successful brands protecting their IP assets
              </p>
              {detectedCountry && (
                <div className="mt-1 sm:mt-2 flex items-center gap-1.5 text-[10px] sm:text-xs text-gray-500">
                  <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                  </svg>
                  <span>Detected: {detectedCountry.city ? `${detectedCountry.city}, ` : ''}{detectedCountry.countryName}</span>
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <label className="block text-[10px] sm:text-xs font-semibold text-gray-700 mb-0.5 sm:mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full h-9 sm:h-10 px-2.5 sm:px-3 text-xs sm:text-sm border-2 border-gray-200 rounded-lg focus:border-[#AB2346] focus:ring-1 focus:ring-[#AB2346]/20 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-[10px] sm:text-xs font-semibold text-gray-700 mb-0.5 sm:mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    placeholder="john@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    onBlur={handleEmailBlur}
                    className={`w-full h-9 sm:h-10 px-2.5 sm:px-3 text-xs sm:text-sm border-2 rounded-lg focus:border-[#AB2346] focus:ring-1 focus:ring-[#AB2346]/20 transition-all ${errors.email ? 'border-red-500' : 'border-gray-200'}`}
                    required
                  />
                </div>
              </div>

              {errors.email && (
                <p className="text-red-500 text-[10px] sm:text-xs font-medium flex items-start gap-1 -mt-1">
                  <span>⚠️</span>
                  <span>{errors.email}</span>
                </p>
              )}

              <div>
                <label className="block text-[10px] sm:text-xs font-semibold text-gray-700 mb-0.5 sm:mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-1.5 sm:gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="w-16 sm:w-20 h-9 sm:h-10 px-1 sm:px-1.5 text-[10px] sm:text-xs border-2 border-gray-200 rounded-lg bg-white font-medium text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#AB2346]/20 focus:border-[#AB2346] cursor-pointer transition-all"
                  >
                    {countryCodes.map((country, index) => (
                      <option key={index} value={country.code}>
                        {country.flag} {country.code}
                      </option>
                    ))}
                  </select>
                  <Input
                    type="tel"
                    placeholder="123-456-7890"
                    value={formData.phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d\s\-]/g, '');
                      setFormData({ ...formData, phone: value });
                    }}
                    onBlur={handlePhoneBlur}
                    className={`flex-1 h-9 sm:h-10 px-2.5 sm:px-3 text-xs sm:text-sm border-2 rounded-lg focus:border-[#AB2346] focus:ring-1 focus:ring-[#AB2346]/20 transition-all ${errors.phone ? 'border-red-500' : 'border-gray-200'}`}
                    required
                  />
                </div>
                {errors.phone && (
                  <p className="text-red-500 text-[10px] sm:text-xs font-medium flex items-start gap-1 mt-0.5 sm:mt-1">
                    <span>⚠️</span>
                    <span>{errors.phone}</span>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[10px] sm:text-xs font-semibold text-gray-700 mb-0.5 sm:mb-1">
                  What service interests you? <span className="text-red-500">*</span>
                </label>
                <Textarea
                  placeholder="E.g., Trademark registration..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={2}
                  className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border-2 border-gray-200 rounded-lg resize-none focus:border-[#AB2346] focus:ring-1 focus:ring-[#AB2346]/20 transition-all"
                />
              </div>

              <div className="flex items-start gap-1.5 sm:gap-2 bg-gray-50 p-1.5 sm:p-2.5 rounded-lg border border-gray-200">
                <input
                  type="checkbox"
                  id="agree-terms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#F97316] border-gray-300 rounded focus:ring-1 focus:ring-[#AB2346]/20 cursor-pointer flex-shrink-0"
                  required
                />
                <label htmlFor="agree-terms" className="text-[10px] sm:text-xs text-gray-600 cursor-pointer leading-tight">
                  Yes, I want to claim my FREE strategy session. I agree to receive expert guidance.
                </label>
              </div>

              <Button
                type="submit"
                disabled={isLoading || !agreedToTerms}
                className="w-full bg-gradient-to-r from-[#012169] via-[#8B1587] to-[#AB2346] hover:shadow-[0_10px_40px_rgba(171,35,70,0.5)] text-white font-bold text-xs sm:text-sm h-9 sm:h-11 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] group relative overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-[#F97316]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></span>
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2 relative z-10">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    <span>Processing...</span>
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2 relative z-10">
                    <span>Claim My FREE Strategy Session →</span>
                  </span>
                )}
              </Button>
              
              <p className="text-center text-xs text-gray-500">
                🔒 100% Secure & Confidential | No Credit Card Required
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InquiryModal;
